import React, { useCallback, useEffect, useState } from 'react'
import { useUserContext } from 'context'
import { useFetchAPI, useWindowResize, useAlert } from 'hooks'
import AlertModal from './AlertModal'
import RebrandAlert from './RebrandAlert/index'
import SchemaSectionRenderer from '../../../components_v1/SchemaSectionRenderer'
import RefundConfirmationModal from '../../../components_v2/AllFree/RefundConfirmationModal/index'
import { AppleRefundModal } from './AppleRefundModal/AppleRefundModal'
import CreatePlayList from '../../MyPractice/CreatePlayList'
import { axios } from 'api'
import { getLocale, DEFAULT_LOCALE } from 'helpers/getLocale'
import { OG_TITLE } from 'components_v2/common'
import { useTitle } from 'hooks/useMetaTitle'
import { HomeContext } from 'components_v2/page_context'
import NewUserPopup from './NewUserPopup/NewUserPopup'

const REBRAND_KEY = 'moves_rebrand'
const APPLE_REFUND_KEY = 'awc_apple_refund_popup'
const NEW_VERIFIED_USER_KEY = 'new_verified_user'

const schemaUrl = (loc, isMobile) =>
  `/homepage/recommendations/schema?device=${
    isMobile ? 'mobile' : 'desktop'
  }&locale=${loc}`

const Home = () => {
  const { user, updateContextData, setSnapShot } = useUserContext()
  const [showRebrand, setShowRebrand] = useState(false)
  const [createInitialPlaylist, setCreateInitialPlaylist] = useState(false)
  const [isCreatePlaylist, setIsCreatePlayList] = useState(false)
  const { width } = useWindowResize()
  const [alertList, setAlertList] = useState(null)
  const [silentAloSignIn, setSilentAloSignIn] = useState(false)
  const [schema, setSchema] = useState([])
  const isMobile = width <= 480
  const setAlert = useAlert()
  useTitle(OG_TITLE)
  const [surveyResponse] = useFetchAPI('/surveys/progress?type=onboarding')
  const fetchSchema = useCallback(
    (loc) => {
      axios
        .get(schemaUrl(loc, isMobile))
        .then((res) => {
          setSchema(res.data)
        })
        .catch((err) => console.log(err))
    },
    [isMobile]
  )

  useEffect(() => {
    const loadSchema = async () => {
      const localeParam = new URLSearchParams(window.location.search).get(
        'locale'
      )
      if (localeParam) {
        fetchSchema(localeParam)
      } else {
        let locale = DEFAULT_LOCALE
        try {
          locale = await getLocale()
        } catch (err) {
          console.log(err)
        }
        fetchSchema(locale)
      }
    }
    loadSchema()
  }, [fetchSchema])

  useEffect(() => {
    updateContextData(
      'user_answered_survey',
      Boolean(surveyResponse.data?.lastCompletedAt)
    )
  }, [surveyResponse])

  useEffect(() => {
    if (localStorage.getItem('signupSuccess') === 'true') {
      setAlert(
        {
          message: 'Success! You’re in the club!'
        },
        { autoClose: 3000 }
      )
      localStorage.removeItem('signupSuccess')
    }
  }, [])

  useEffect(() => {
    axios
      .get('/user_popups')
      .then((res) => {
        setAlertList(res?.data?.popups || [])
        setSilentAloSignIn(Boolean(res?.data?.silentAloSignin))
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRebrand(true)
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get('/my_practice/homepage_summary')
        if (res) {
          setSnapShot(res.data)
        }
      } catch (e) {
        console.log('e', e)
      }
    }
    fetchUser()
  }, [createInitialPlaylist])

  const handlePopupClose = (popup_key) => {
    setAlertList((state) => state?.filter((i) => i !== popup_key))
    axios
      .post('/user_popups', { popup_key })
      .then((res) => {
        if (res?.data?.message) {
          console.log(res.data.message)
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const urlParam = new URLSearchParams(window.location.search)
  const showRefundConfirmationModal =
    urlParam.get('show_refund_popup') === 'true'

  return (
    <HomeContext.Provider value={{ setIsCreatePlayList }}>
      <SchemaSectionRenderer
        schema={schema}
        setIsCreatePlayList={setIsCreatePlayList}
        user={user}
        location='homepage'
      />
      {alertList?.includes(NEW_VERIFIED_USER_KEY) && (
        <NewUserPopup
          handleClose={() => handlePopupClose(NEW_VERIFIED_USER_KEY)}
          showSignOut={silentAloSignIn}
          email={user?.email}
        />
      )}
      {alertList?.length === 0 &&
        Boolean(surveyResponse.data?.lastCompletedAt) && (
          <AlertModal data={surveyResponse.data} />
        )}
      {showRebrand &&
        !alertList?.includes(NEW_VERIFIED_USER_KEY) &&
        alertList?.includes(REBRAND_KEY) && (
          <RebrandAlert
            data={surveyResponse.data}
            handleClose={() => handlePopupClose(REBRAND_KEY)}
          />
        )}
      {alertList?.includes(APPLE_REFUND_KEY) && <AppleRefundModal />}

      {showRefundConfirmationModal && <RefundConfirmationModal />}
      <CreatePlayList
        isOpen={isCreatePlaylist}
        onClose={() => setIsCreatePlayList(false)}
        onSuccess={() => {
          setAlert(
            {
              message:
                'Success! Playlist created. Add classes to your new playlist as you browse.'
            },
            { autoClose: 3000 }
          )
          setCreateInitialPlaylist((value) => !value)
        }}
      />
    </HomeContext.Provider>
  )
}

export default Home
